import { matchPath } from "react-router-dom";

/**
 * Single source of truth for dynamic browser-tab titles (CIQ-11563).
 *
 * The browser tab title is derived from the current route. To add or change a
 * title, edit ONE entry in `TITLE_ROUTES` below.
 *
 * Maintainer rules (so the most-specific route always wins):
 *  1. Give every leaf route `exact: true`. Without it, react-router v5 does a
 *     prefix match, so `/project/:projectId/submittals` would also match
 *     `/project/:projectId/submittals/:submittalId`.
 *  2. Order most-specific first. The resolver returns on the FIRST match, so:
 *       - list children BEFORE their parents, and
 *       - list literal-segment routes BEFORE param-segment routes at the same
 *         depth (e.g. `materials/extracted` before `materials/:materialId`;
 *         otherwise `extracted` would match `:materialId`).
 *
 * Scope: the main BrowserRouter app only. The Admin and Partner HashRouter
 * sub-apps are separate routers and are not covered here.
 */

export const BRAND = "ConstructivIQ";

/** Formats a page name as "<Page> | ConstructivIQ"; brand-only when no page. */
export const formatTitle = (page?: string): string =>
  page ? `${page} | ${BRAND}` : BRAND;

/**
 * Joins an entity's number and name into a tab-title page name
 * (e.g. "SUB-001 — Concrete"). Omits any empty part, and returns `undefined`
 * when both are empty so the route's static title is used instead. Pass the
 * result to `usePageTitle` from a detail page once its data has loaded.
 */
export const formatEntityTitle = (
  number?: string | number | null,
  name?: string | null
): string | undefined => {
  const parts = [number, name]
    .filter(
      (part) => part !== null && part !== undefined && `${part}`.trim() !== ""
    )
    .map((part) => `${part}`.trim());
  return parts.length ? parts.join(" — ") : undefined;
};

export interface TitleRoute {
  path: string | string[];
  exact?: boolean;
  /**
   * Static title, or a function of the matched route params. The function form
   * is reserved for param-driven fallbacks; entity-specific titles (e.g.
   * "SUB-001 — Concrete") are set at runtime via the page-title override.
   */
  title: string | ((params: Record<string, string>) => string);
}

export const TITLE_ROUTES: TitleRoute[] = [
  // ---- Project detail / child routes (more specific) FIRST ----
  {
    path: [
      "/project/:projectId/submittals/:submittalId",
      "/project/:projectId/Submittals/:submittalId"
    ],
    exact: true,
    title: "Submittal Details"
  },
  {
    path: "/project/:projectId/submittals-untracked",
    exact: true,
    title: "Untracked Submittals"
  },
  {
    path: "/project/:projectId/submittals-voided",
    exact: true,
    title: "Voided Submittals"
  },
  {
    path: "/project/:projectId/submittals-deleted",
    exact: true,
    title: "Recycle Bin"
  },
  // literal `extracted` / `sc-request` must precede the `:materialId` param route
  {
    path: "/project/:projectId/materials/extracted",
    exact: true,
    title: "Extracted Materials"
  },
  {
    path: "/project/:projectId/materials/sc-request",
    exact: true,
    title: "SC Request"
  },
  {
    path: [
      "/project/:projectId/materials/:materialId",
      "/project/:projectId/Materials/:materialId"
    ],
    exact: true,
    title: "Material Details"
  },
  {
    path: "/project/:projectId/bid-packages/:bidPackageId",
    exact: true,
    title: "Preconstruction Details"
  },
  {
    path: "/project/:projectId/design-packages/:designPackageId/details",
    exact: true,
    title: "Design Package Details"
  },
  {
    path: "/project/:projectId/design-packages/:designPackageId",
    exact: true,
    title: "Design Packages"
  },

  // reports children before /reports
  {
    path: "/project/:projectId/reports/submittals/export",
    exact: true,
    title: "Risk Report Export"
  },
  {
    path: "/project/:projectId/reports/project-reports",
    exact: true,
    title: "Project Reports"
  },
  {
    path: "/project/:projectId/reports/audit-reports",
    exact: true,
    title: "Audit Reports"
  },
  {
    path: "/project/:projectId/reports/project-reports/risk",
    exact: true,
    title: "Risk Report"
  },
  {
    path: "/project/:projectId/reports/audit-reports/activity-log",
    exact: true,
    title: "Activity Log"
  },
  {
    path: "/project/:projectId/reports/project-reports/upcoming",
    title: "Upcoming Due Dates Review"
  },
  {
    path: "/project/:projectId/reports/project-reports/activities",
    title: "Schedule Lookahead Report"
  },
  {
    path: "/project/:projectId/reports/project-reports/procurement-follow-ups",
    title: "Procurement Follow-Ups"
  },
  {
    path: "/project/:projectId/reports/project-reports/custom-report/:reportId",
    title: "Custom Report"
  },

  // schedule children before /schedule
  {
    path: "/project/:projectId/schedule/project-schedules/opc",
    exact: true,
    title: "Schedules in OPC"
  },
  {
    path: "/project/:projectId/schedule/project-schedules",
    exact: true,
    title: "Schedules"
  },
  {
    path: "/project/:projectId/schedule/project-schedule/:selectedVersionId",
    exact: true,
    title: "Schedule Details"
  },
  {
    path: "/project/:projectId/schedule/change-impact/printview",
    exact: true,
    title: "Change Impact Print"
  },
  {
    path: "/project/:projectId/schedule/change-impact",
    exact: true,
    title: "Change Impact"
  },
  {
    path: "/project/:projectId/schedule/impact/export",
    exact: true,
    title: "Change Impact Export"
  },
  {
    path: "/project/:projectId/schedule/submittal-linking",
    exact: true,
    title: "Submittal Linking"
  },
  {
    path: "/project/:projectId/schedule/material-linking",
    exact: true,
    title: "Material Linking"
  },

  // settings children before /settings (project)
  {
    path: [
      "/project/:projectId/settings/:tab1Id/:tab2Id/:tab3Id",
      "/project/:projectId/settings/:tab1Id/:tab2Id",
      "/project/:projectId/settings/:tab1Id"
    ],
    exact: true,
    title: "Settings"
  },

  // export / print routes
  {
    path: "/project/:projectId/analytics/export",
    exact: true,
    title: "Analytics Export"
  },
  { path: "/project/:projectId/printView", title: "Print" },
  {
    path: "/project/:projectId/custom-excel-export",
    title: "Custom Export"
  },

  // ---- Project top-level Feature screens ----
  { path: "/project/:projectId/dashboard", exact: true, title: "Dashboard" },
  {
    path: "/project/:projectId/submittals",
    exact: true,
    title: "Submittals"
  },
  { path: "/project/:projectId/materials", exact: true, title: "Materials" },
  {
    path: "/project/:projectId/bid-packages",
    exact: true,
    title: "Preconstruction"
  },
  {
    path: "/project/:projectId/design-packages",
    exact: true,
    title: "Design Packages"
  },
  { path: "/project/:projectId/schedule", exact: true, title: "Schedules" },
  { path: "/project/:projectId/reports", exact: true, title: "Reports" },
  { path: "/project/:projectId/settings", exact: true, title: "Settings" },
  { path: "/project/:projectId/design", exact: true, title: "Design" },

  // ---- Home / subscription-level Feature screens ----
  { path: "/home", exact: true, title: "Home" },
  { path: "/new-project", exact: true, title: "New Project" },
  { path: "/projects", exact: true, title: "Projects" },
  { path: "/users", exact: true, title: "Users" },
  { path: "/companies", exact: true, title: "Companies" },
  { path: "/analytics/export", exact: true, title: "Analytics Export" },
  { path: "/analytics", exact: true, title: "Analytics" },
  {
    path: "/reports/audit-reports/activity-log",
    exact: true,
    title: "Activity Log"
  },
  { path: "/reports/audit-reports", exact: true, title: "Audit Reports" },
  { path: "/reports/printView", exact: true, title: "Print" },
  { path: "/reports", exact: true, title: "Reports" },
  {
    path: [
      "/settings/:tab1Id/:tab2Id/:tab3Id",
      "/settings/:tab1Id/:tab2Id",
      "/settings/:tab1Id",
      "/settings"
    ],
    exact: true,
    title: "Settings"
  },
  { path: "/change_password", exact: true, title: "Change Password" },

  // ---- Public / auth routes ----
  { path: "/", exact: true, title: "Login" },
  { path: "/Login", exact: true, title: "Login" },
  { path: "/subscription-select", exact: true, title: "Select Subscription" },
  { path: "/accept-invite", exact: true, title: "Accept Invitation" },
  { path: "/forgot_password", exact: true, title: "Forgot Password" },
  { path: "/reset_password", exact: true, title: "Reset Password" },
  { path: "/set_password", exact: true, title: "Set Password" },
  { path: "/reset_expired_password", exact: true, title: "Reset Password" }
];

/**
 * Resolves the formatted document title for a pathname. Iterates `TITLE_ROUTES`
 * top-down and returns the first match; falls back to the brand when nothing
 * matches (e.g. unknown URLs / Not Found).
 */
export function resolveTitle(pathname: string): string {
  for (const route of TITLE_ROUTES) {
    const match = matchPath(pathname, {
      path: route.path,
      exact: route.exact ?? false,
      strict: false
    });
    if (match) {
      const page =
        typeof route.title === "function"
          ? route.title(match.params)
          : route.title;
      return formatTitle(page);
    }
  }
  return formatTitle();
}
